import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, PlayCircle, X } from 'lucide-react';
import { videoGuidaEmbedUrl, videoGuidaWatchUrl } from './videoGuida';

/**
 * Pulsante «Video guida» + finestra col video YouTube (verbali, dipendenti dello studio, badge).
 * Dominio `youtube-nocookie.com`: l'iframe si monta solo a finestra aperta.
 */
export interface VideoGuidaFinestraProps {
  videoId: string;
  titolo: string;
  /** Es. «5 minuti», accanto al titolo della finestra. */
  durata?: string;
  /** `testata`: pulsante chiaro su fondo colorato; `pagina`: pulsante su fondo bianco; `compatto`: accanto ai pulsanti piccoli di una card. */
  variante?: 'testata' | 'pagina' | 'compatto';
}

const STILE_PULSANTE: Record<NonNullable<VideoGuidaFinestraProps['variante']>, string> = {
  testata: 'gap-1.5 rounded-full px-3.5 py-2 text-sm border-white/30 bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white',
  pagina: 'gap-1.5 rounded-full px-3.5 py-2 text-sm border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 focus-visible:ring-emerald-500 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
  compatto: 'gap-1 rounded-lg px-3 py-1.5 text-xs border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 focus-visible:ring-emerald-500 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:hover:bg-emerald-900/50',
};

const VideoGuidaFinestra: React.FC<VideoGuidaFinestraProps> = ({ videoId, titolo, durata, variante = 'pagina' }) => {
  const [aperto, setAperto] = useState(false);
  const riquadro = useRef<HTMLDivElement>(null);
  const chiudiRef = useRef<HTMLButtonElement>(null);
  const idTitolo = `video-guida-${videoId}-titolo`;

  useEffect(() => {
    if (!aperto) return undefined;
    const suTasto = (e: KeyboardEvent) => { if (e.key === 'Escape') setAperto(false); };
    document.addEventListener('keydown', suTasto);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    chiudiRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', suTasto);
      document.body.style.overflow = overflow;
    };
  }, [aperto]);

  // Schermo intero sul riquadro del video; senza Fullscreen API (es. iOS Safari
  // sui div) si apre il video su YouTube in una nuova scheda.
  const schermoIntero = () => {
    const el = riquadro.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    const suYouTube = () => { window.open(videoGuidaWatchUrl(videoId), '_blank', 'noopener,noreferrer'); };
    if (el?.requestFullscreen) el.requestFullscreen().catch(suYouTube);
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else suYouTube();
  };

  const icona = variante === 'compatto' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <>
      <button
        type="button"
        onClick={() => setAperto(true)}
        className={`inline-flex items-center border font-medium transition-colors focus:outline-none focus-visible:ring-2 ${STILE_PULSANTE[variante]}`}
      >
        <PlayCircle className={icona} aria-hidden="true" />
        Video guida
      </button>

      {aperto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={idTitolo}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAperto(false)} aria-hidden="true" />
          <div className="relative w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
              <h2 id={idTitolo} className="flex-1 text-sm font-semibold text-gray-900 dark:text-white">
                {titolo}
                {durata && <> <span className="font-normal text-gray-500 dark:text-gray-400">({durata})</span></>}
              </h2>
              <button
                type="button"
                onClick={schermoIntero}
                className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" /> Schermo intero
              </button>
              <button
                ref={chiudiRef}
                type="button"
                onClick={() => setAperto(false)}
                aria-label="Chiudi"
                className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="p-4">
              <div ref={riquadro} className="overflow-hidden rounded-xl bg-black">
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                  <iframe
                    src={videoGuidaEmbedUrl(videoId, true)}
                    title={titolo}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoGuidaFinestra;
